/* eslint-disable no-useless-escape */
import { PageType, PageContext, VehicleContext } from '../types';

/**
 * Detect the current page type based on URL patterns
 */
export const detectPageType = (url: string): PageType => {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('/inventory') || lowerUrl.includes('/vehicles') || lowerUrl.includes('/cars')) {
    // Check if it's a specific vehicle page (VDP) or general inventory
    if (lowerUrl.match(/\/(vehicle|car|vdp)\/[a-zA-Z0-9-]+/)) {
      return 'vdp';
    }
    return 'inventory';
  }

  if (lowerUrl.includes('/service') || lowerUrl.includes('/maintenance')) {
    return 'service';
  }

  if (lowerUrl.includes('/finance') || lowerUrl.includes('/financing') || lowerUrl.includes('/credit')) {
    return 'finance';
  }

  if (lowerUrl === '/' || lowerUrl.includes('/home') || lowerUrl.includes('/index')) {
    return 'homepage';
  }

  return 'other';
};

/**
 * Extract vehicle context from the current page
 * This is a basic implementation - can be enhanced to scrape actual page data
 */
export const extractVehicleContext = (): VehicleContext | undefined => {
  // Try to extract from page title
  const title = document.title;

  // Try to extract from URL
  const url = window.location.href;

  // Look for common patterns in URL
  const vinMatch = url.match(/vin[=\/]([A-HJ-NPR-Z0-9]{17})/i);
  const stockMatch = url.match(/stock[=\/]([A-Z0-9-]+)/i);

  // Try to extract from structured data
  const structuredData = extractStructuredData();
  if (structuredData) {
    return structuredData;
  }

  // Try to extract from meta tags
  const metaData = extractFromMetaTags();
  if (metaData) {
    return metaData;
  }

  // Basic extraction from title (e.g., "2020 Honda CR-V - Dealer Name")
  const yearMatch = title.match(/\b(19|20)\d{2}\b/);
  const year = yearMatch ? parseInt(yearMatch[0]) : undefined;

  const context: VehicleContext = {
    year,
    vin: vinMatch ? vinMatch[1] : undefined,
    stockNumber: stockMatch ? stockMatch[1] : undefined,
  };

  return Object.values(context).some(v => v !== undefined) ? context : undefined;
};

/**
 * Extract vehicle data from JSON-LD structured data
 */
const extractStructuredData = (): VehicleContext | undefined => {
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');

    for (const script of Array.from(scripts)) {
      try {
        const data = JSON.parse(script.textContent || '');

        if (data['@type'] === 'Car' || data['@type'] === 'Vehicle') {
          return {
            make: data.brand?.name || data.manufacturer?.name,
            model: data.model,
            year: data.modelYear ? parseInt(data.modelYear) : data.productionDate ? new Date(data.productionDate).getFullYear() : undefined,
            price: data.offers?.price ? parseFloat(data.offers.price) : undefined,
            vin: data.vehicleIdentificationNumber,
            mileage: data.mileageFromOdometer?.value ? parseInt(data.mileageFromOdometer.value) : undefined,
            bodyStyle: data.bodyType,
          };
        }
      } catch (e) {
        // Skip invalid JSON
        continue;
      }
    }
  } catch (error) {
    console.error('[Miles Widget] Error extracting structured data:', error);
  }

  return undefined;
};

/**
 * Extract vehicle data from meta tags
 */
const extractFromMetaTags = (): VehicleContext | undefined => {
  try {
    const getMeta = (name: string): string | undefined => {
      const meta = document.querySelector(`meta[property="${name}"], meta[name="${name}"]`);
      return meta?.getAttribute('content') || undefined;
    };

    const make = getMeta('og:make') || getMeta('vehicle:make');
    const model = getMeta('og:model') || getMeta('vehicle:model');
    const year = getMeta('og:year') || getMeta('vehicle:year');
    const price = getMeta('og:price') || getMeta('vehicle:price');
    const vin = getMeta('vehicle:vin');

    if (make || model || year || vin) {
      return {
        make,
        model,
        year: year ? parseInt(year) : undefined,
        price: price ? parseFloat(price.replace(/[^0-9.]/g, '')) : undefined,
        vin,
      };
    }
  } catch (error) {
    console.error('[Miles Widget] Error extracting meta tags:', error);
  }

  return undefined;
};

/**
 * Get the full page context
 */
export const getPageContext = (): PageContext => {
  const url = window.location.href;
  const type = detectPageType(url);
  const vehicleContext = type === 'vdp' ? extractVehicleContext() : undefined;

  return {
    type,
    url,
    vehicleContext,
  };
};

/**
 * Determine if the current page should trigger the glow state
 */
export const shouldShowGlow = (pageType: PageType): boolean => {
  const importantPages: PageType[] = ['homepage', 'inventory', 'vdp', 'service', 'finance'];
  return importantPages.includes(pageType);
};
